/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import {
  Save, RefreshCw, UserPlus, Users, Clock, Shield, Link, Settings,
  CheckCircle2, XCircle, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = ['new', 'contacted', 'proposal_sent', 'negotiation', 'follow_up', 'won', 'lost', 'ghosted'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

export default function MetaAdsSettings({ onBack }) {
  const { getAuthHeaders } = useAdmin();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = getAuthHeaders();
  const hdrs = { ...headers, 'Content-Type': 'application/json' };

  const fetchSettings = useCallback(async () => {
    try {
      const hdrs = getAuthHeaders();
      const [sRes, uRes, syncRes] = await Promise.all([
        fetch(`${API_URL}/api/meta-ads/settings`, { headers: hdrs }),
        fetch(`${API_URL}/api/meta-ads/users`, { headers: hdrs }),
        fetch(`${API_URL}/api/meta-ads/auto-sync/status`, { headers: hdrs }),
      ]);
      setSettings(await sRes.json());
      setUsers((await uRes.json()).users || []);
      setSyncStatus(await syncRes.json());
    } catch (e) { logger.error(e); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/meta-ads/settings`, {
        method: 'PATCH', headers: hdrs,
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        toast.success('Settings saved');
      } else {
        toast.error('Failed to save');
      }
    } catch (e) { toast.error('Error saving settings'); }
    finally { setSaving(false); }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleRoundRobinAgent = (userId) => {
    const agents = settings.round_robin_agents || [];
    if (agents.includes(userId)) {
      updateSetting('round_robin_agents', agents.filter(id => id !== userId));
    } else {
      updateSetting('round_robin_agents', [...agents, userId]);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400"><RefreshCw className="animate-spin" /></div>;
  if (!settings) return <div className="text-center py-12 text-slate-400">Failed to load settings</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="meta-ads-settings">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-500 hover:text-blue-600" data-testid="settings-back">
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-xl font-bold text-slate-900">Meta Ads Settings</h2>
      </div>

      {/* Auto-Assignment */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <UserPlus size={16} /> Auto-Assignment Rules
        </h3>
        <p className="text-xs text-slate-400 mb-4">How new Meta leads are assigned when they arrive</p>

        <div className="space-y-3">
          {[
            { value: 'unassigned', label: 'Leave unassigned' },
            { value: 'specific', label: 'Assign to specific agent' },
            { value: 'round_robin', label: 'Round-robin among selected agents' },
          ].map(opt => (
            <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
              <input type="radio" name="auto_assignment" value={opt.value}
                checked={settings.auto_assignment === opt.value}
                onChange={e => updateSetting('auto_assignment', e.target.value)}
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </label>
          ))}
        </div>

        {settings.auto_assignment === 'specific' && (
          <select value={settings.assigned_agent || ''}
            onChange={e => updateSetting('assigned_agent', e.target.value)}
            className="mt-3 border rounded-lg px-3 py-2 text-sm bg-white w-full" data-testid="specific-agent-select">
            <option value="">Select agent...</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
        )}

        {settings.auto_assignment === 'round_robin' && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-slate-400">Select agents for round-robin:</p>
            {users.map(u => (
              <label key={u.id} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox"
                  checked={(settings.round_robin_agents || []).includes(u.id)}
                  onChange={() => toggleRoundRobinAgent(u.id)}
                  className="rounded"
                />
                <span className="text-sm text-slate-700">{u.name} ({u.email})</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Sync Settings */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Clock size={16} /> Sync Settings
        </h3>
        <p className="text-xs text-slate-400 mb-4">How often leads are automatically pulled from Meta</p>

        {syncStatus && (
          <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
            syncStatus.enabled ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-slate-50 text-slate-500 border border-slate-200'
          }`} data-testid="auto-sync-status">
            {syncStatus.enabled ? (
              <>
                <CheckCircle2 size={16} className="text-green-500" />
                <span>Auto-sync active — every <strong>{syncStatus.frequency}</strong></span>
                {syncStatus.next_run && (
                  <span className="ml-auto text-xs text-green-500">
                    Next: {new Date(syncStatus.next_run).toLocaleTimeString()}
                  </span>
                )}
              </>
            ) : (
              <>
                <XCircle size={16} className="text-slate-400" />
                <span>Auto-sync is disabled</span>
              </>
            )}
          </div>
        )}

        <div className="space-y-2">
          {[
            { value: '15m', label: 'Every 15 minutes' },
            { value: '1h', label: 'Every 1 hour' },
            { value: '6h', label: 'Every 6 hours' },
            { value: '24h', label: 'Every 24 hours' },
            { value: 'disabled', label: 'Disabled (manual + webhook only)' },
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
              <input type="radio" name="sync_frequency" value={opt.value}
                checked={settings.sync_frequency === opt.value}
                onChange={e => updateSetting('sync_frequency', e.target.value)}
              />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={settings.webhook_enabled}
              onChange={e => updateSetting('webhook_enabled', e.target.checked)}
              className="rounded"
            />
            <div>
              <span className="text-sm font-medium text-slate-700">Enable Webhook</span>
              <p className="text-xs text-slate-400">Receive leads instantly when they're submitted on Meta</p>
            </div>
          </label>
          {settings.webhook_enabled && (
            <div className="mt-2 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Webhook URL (configure in Meta Developer Console):</p>
              <code className="text-xs text-blue-600 break-all">{API_URL}/api/meta-ads/webhook/receive</code>
            </div>
          )}
        </div>
      </div>

      {/* Duplicate Detection */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Shield size={16} /> Duplicate Detection
        </h3>
        <p className="text-xs text-slate-400 mb-4">How duplicates are detected and handled</p>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={settings.duplicate_check_phone}
              onChange={e => updateSetting('duplicate_check_phone', e.target.checked)} className="rounded" />
            <span className="text-sm text-slate-700">Check by phone number (exact match)</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={settings.duplicate_check_email}
              onChange={e => updateSetting('duplicate_check_email', e.target.checked)} className="rounded" />
            <span className="text-sm text-slate-700">Check by email address (exact match)</span>
          </label>
        </div>

        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-slate-400 mb-2">When duplicate found:</p>
          <div className="space-y-2">
            {[
              { value: 'skip', label: 'Skip and flag as duplicate' },
              { value: 'update', label: 'Update existing record' },
              { value: 'create', label: 'Always create new record' },
            ].map(opt => (
              <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                <input type="radio" name="duplicate_action" value={opt.value}
                  checked={settings.duplicate_action === opt.value}
                  onChange={e => updateSetting('duplicate_action', e.target.value)} />
                <span className="text-sm text-slate-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Default Stage & Priority */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Settings size={16} /> Defaults
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Default Lead Stage</label>
            <select value={settings.default_stage} onChange={e => updateSetting('default_stage', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize">
              {STAGES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Default Priority</label>
            <select value={settings.default_priority} onChange={e => updateSetting('default_priority', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-white capitalize">
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveSettings} disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6" data-testid="save-settings-btn">
          {saving ? <RefreshCw className="animate-spin mr-2" size={14} /> : <Save className="mr-2" size={14} />}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
